import React, { useEffect, useRef, useState } from "react";
import { Container, Card, CardBody, Row, Col, Spinner, Input, Button, Badge } from "reactstrap";
import { Link, useParams } from "react-router-dom";
import { useAuthUser } from "../../../hooks/AuthProvider";
import {
  getListingById, getListingInquiriesByListing, onListingInquiryMessages,
  addListingInquiryMessage, markListingInquiryRead,
} from "../../../helpers/firestoreService";
import PageHero from "../../../components/Common/PageHero";

/** One listing's inquiries, grouped by prospect — pick one to open the thread. */
const ListingInquiriesPage = () => {
  document.title = "Listing Inquiries | Support Portal";

  const { listingId } = useParams();
  const user = useAuthUser();
  const [listing, setListing] = useState(null);
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const boxRef = useRef(null);

  useEffect(() => {
    if (!listingId || !user?.uid) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [l, inqs] = await Promise.all([
          getListingById(listingId),
          getListingInquiriesByListing(listingId, user.uid),
        ]);
        if (cancelled) return;
        setListing(l);
        setInquiries(inqs);
        if (inqs.length > 0) setSelectedId(inqs[0].id);
      } catch (e) {
        console.error("Load listing inquiries error:", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [listingId, user?.uid]);

  const selected = inquiries.find((i) => i.id === selectedId) || null;

  useEffect(() => {
    if (!selected?.id || !user?.uid) return;
    const unsubscribe = onListingInquiryMessages(selected.id, user.uid, "pm", setMessages);
    markListingInquiryRead(selected.id, "pm")
      .then(() => {
        setInquiries((prev) => prev.map((i) => (i.id === selected.id ? { ...i, unreadByPm: false } : i)));
      })
      .catch(() => {});
    return () => unsubscribe();
  }, [selected?.id, user?.uid]);

  useEffect(() => {
    if (boxRef.current) boxRef.current.scrollTop = boxRef.current.scrollHeight;
  }, [messages]);

  const handleSend = async () => {
    const body = text.trim();
    if (!body || !selected?.id || !user?.uid) return;
    setSending(true);
    setError("");
    try {
      await addListingInquiryMessage(selected.id, {
        senderUid: user.uid,
        senderRole: "pm",
        senderName: user.displayName || user.email || "",
        senderEmail: user.email || "",
        text: body,
      });
      setText("");
    } catch (e) {
      console.error("Send listing inquiry reply error:", e);
      setError("Message couldn't be sent. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="page-content">
      <Container fluid>
        <PageHero
          title={listing?.address ? `Inquiries — ${listing.address}` : "Listing Inquiries"}
          subtitle="Every prospect who messaged in about this unit, grouped by conversation."
        >
          <Link to="/listings" className="btn btn-light btn-sm">
            <i className="bx bx-arrow-back me-1" /> All Listings
          </Link>
        </PageHero>

        {loading ? (
          <div className="text-center py-5"><Spinner color="primary" /></div>
        ) : inquiries.length === 0 ? (
          <Card>
            <CardBody className="text-center py-5">
              <i className="bx bx-conversation text-muted" style={{ fontSize: "2.5rem" }} />
              <h5 className="text-muted mt-2">No inquiries yet</h5>
              <p className="text-muted mb-0">Messages from prospects viewing this listing will show up here.</p>
            </CardBody>
          </Card>
        ) : (
          <Row className="g-3">
            <Col md={4} lg={3}>
              <Card className="h-100">
                <CardBody className="p-2">
                  <div className="list-group list-group-flush" style={{ maxHeight: 560, overflowY: "auto" }}>
                    {inquiries.map((inq) => (
                      <button
                        key={inq.id}
                        type="button"
                        className={`list-group-item list-group-item-action text-start ${inq.id === selectedId ? "active" : ""}`}
                        onClick={() => setSelectedId(inq.id)}
                      >
                        <div className="d-flex justify-content-between align-items-start">
                          <div className="text-truncate fw-semibold">{inq.inquirerName || inq.inquirerEmail}</div>
                          {inq.unreadByPm && <Badge color="danger" pill>New</Badge>}
                        </div>
                        <small className={inq.id === selectedId ? "text-white-50" : "text-muted"}>
                          {inq.inquirerEmail}
                        </small>
                        <div className={`small text-truncate ${inq.id === selectedId ? "text-white-50" : "text-muted"}`}>
                          {inq.lastMessage || "No messages yet"}
                        </div>
                      </button>
                    ))}
                  </div>
                </CardBody>
              </Card>
            </Col>

            <Col md={8} lg={9}>
              <Card className="h-100">
                <CardBody>
                  {selected ? (
                    <>
                      <div className="mb-3">
                        <h5 className="mb-0">{selected.inquirerName || selected.inquirerEmail}</h5>
                        <small className="text-muted">{selected.inquirerEmail}</small>
                      </div>
                      <div
                        ref={boxRef}
                        className="overflow-auto p-3 border rounded mb-2"
                        style={{ height: 380, background: "#fbfbfd" }}
                      >
                        {messages.length === 0 ? (
                          <div className="text-center text-muted py-5">
                            <p className="small mb-0">No messages yet.</p>
                          </div>
                        ) : (
                          messages.map((m) => {
                            const isSelf = m.senderRole === "pm";
                            return (
                              <div key={m.id} className={`d-flex mb-2 ${isSelf ? "justify-content-end" : "justify-content-start"}`}>
                                <div
                                  className={`rounded px-3 py-2 ${isSelf ? "bg-primary text-white" : "bg-white border"}`}
                                  style={{ maxWidth: "80%", fontSize: "0.85rem" }}
                                >
                                  <span style={{ whiteSpace: "pre-wrap" }}>{m.text}</span>
                                </div>
                              </div>
                            );
                          })
                        )}
                      </div>
                      <div className="d-flex gap-2">
                        <Input
                          type="textarea"
                          rows={2}
                          bsSize="sm"
                          placeholder="Reply…"
                          value={text}
                          onChange={(e) => setText(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && !e.shiftKey) {
                              e.preventDefault();
                              handleSend();
                            }
                          }}
                          style={{ resize: "none" }}
                        />
                        <Button color="primary" size="sm" disabled={!text.trim() || sending} onClick={handleSend}>
                          {sending ? <Spinner size="sm" /> : <i className="bx bx-send" />}
                        </Button>
                      </div>
                      {error && <div className="text-danger small mt-1">{error}</div>}
                    </>
                  ) : (
                    <p className="text-muted small fst-italic mb-0">Pick a conversation to open it.</p>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}
      </Container>
    </div>
  );
};

export default ListingInquiriesPage;
